import { Callout, Stack, FormField, Button, Inline, styled, css, TextInput } from 'canoe';
import React, { ChangeEvent, useRef, useState } from 'react';

import { ShadowProperties } from './types';
import { parseShadowValue, renderShadowProperties, wrapPixelValue } from './utils';

import { ColorPicker } from '../ColorPicker';
import { Input } from '../Input';

interface ShadowPickerProps {
  value: string;
  onChange: (value: string) => void;
  hasSpread?: boolean;
  disabled?: boolean;
}

const Container = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.White};
    box-shadow: ${theme.boxShadow.default()};
    display: flex;
    flex-direction: column;
    padding: ${theme.spaces.normal};
    border-radius: ${theme.borderRadius.regular};
  `}
`;

export const ShadowPicker = React.memo(
  ({ value: shadowText, onChange, hasSpread = true }: ShadowPickerProps) => {
    const shadow = parseShadowValue(shadowText, hasSpread);
    const [textInputValue, setTextInputValue] = useState<string>(shadowText);
    const updateShadow = (update: Partial<ShadowProperties>) => {
      const newValue = renderShadowProperties({ ...shadow, ...update, hasSpread });
      onChange(newValue);
      setTextInputValue(newValue);
    };
    const containerRef = useRef<HTMLDivElement>(null);

    const [pickerVisible, setPickerVisible] = useState(false);

    const onClear = () =>
      updateShadow({ offsetX: '0px', offsetY: '0px', blur: '0px', spread: '0px' });

    const onUpdateText = (e: ChangeEvent<HTMLInputElement>) => {
      setTextInputValue(e.target.value);
      onChange(e.target.value);
    };

    return (
      <div ref={containerRef}>
        <Inline>
          <TextInput value={textInputValue} onChange={onUpdateText} />
          <Button
            appearance="outline"
            variant="contrast"
            onClick={() => window.requestAnimationFrame(() => setPickerVisible(true))}
          >
            Edit
          </Button>
        </Inline>
        {pickerVisible && (
          <Callout
            usePortal={false}
            referenceElement={containerRef.current}
            onClose={() => setPickerVisible(false)}
            closeKey="Escape"
            placement="top-end"
          >
            {(calloutProps) => (
              <div
                ref={calloutProps.ref}
                style={{ ...calloutProps.style, zIndex: 1 }}
                {...calloutProps.attributes.popper}
              >
                <Container>
                  <Stack>
                    <ColorPicker
                      onChange={(value: string) => updateShadow({ color: value })}
                      value={shadow.color}
                    />
                    <Inline>
                      <FormField label="Offset X" scale="small">
                        <Input
                          type="unit"
                          onChange={(value: string) =>
                            updateShadow({ offsetX: wrapPixelValue(value) })
                          }
                          value={shadow.offsetX}
                        />
                      </FormField>
                      <FormField label="Blur" scale="small">
                        <Input
                          type="unit"
                          onChange={(value: string) =>
                            updateShadow({ blur: wrapPixelValue(value) })
                          }
                          value={shadow.blur}
                        />
                      </FormField>
                    </Inline>
                    <Inline>
                      <FormField label="Offset Y" scale="small">
                        <Input
                          type="unit"
                          onChange={(value: string) =>
                            updateShadow({ offsetY: wrapPixelValue(value) })
                          }
                          value={shadow.offsetY}
                        />
                      </FormField>
                      {hasSpread && (
                        <FormField label="Spread" scale="small">
                          <Input
                            type="unit"
                            onChange={(value: string) =>
                              updateShadow({ spread: wrapPixelValue(value) })
                            }
                            value={shadow.spread}
                          />
                        </FormField>
                      )}
                    </Inline>
                    <Button appearance="text" onClick={onClear}>
                      Clear
                    </Button>
                  </Stack>
                </Container>
              </div>
            )}
          </Callout>
        )}
      </div>
    );
  },
);
